/**
 * Circuit breaker on every request the Supabase client sends from this tab.
 *
 * WHY: on 2026-10-19 a render loop on the Festival Board (#1346, fixed #1348)
 * fired ~2,200 identical RPCs from ONE tab in about two minutes (~10/s per
 * RPC). PostgREST connections doubled, the ~2 GB database ran out of memory
 * under the newsletter's write load, and it stopped answering for 19 minutes
 * while tickets were selling (see wiki log 2026-10-19). A bug in any page must
 * never again be able to take the database down, so the client refuses to
 * send a request that is clearly looping, whatever page it comes from.
 *
 * Thresholds come from live edge logs (24h to 2026-10-19 03:30Z, browsers
 * only, the incident excluded): the busiest identical request was 24 in 10 s
 * and the busiest client sent 156 requests in a minute. The loop sent ~100
 * identical requests per 10 s and ~1,200 a minute. Limits sit well above
 * normal use and trip within seconds of a loop:
 * - the SAME request (method + URL + body) more than 40 times in 10 s blocks
 *   that request; each repeat trip doubles the block (60 s up to 10 min);
 * - more than 600 requests of any kind in 60 s blocks everything for 30 s.
 *
 * Blocked requests never reach the network: they get a 429 the client turns
 * into an ordinary `{ error }`, so a stuck page shows errors instead of
 * hammering the database.
 *
 * Checkout's own calls (native-complete-checkout etc.) use plain `fetch`, not
 * this client, so this guard cannot block a payment.
 */

export const SAME_REQUEST_LIMIT = 40;
export const SAME_REQUEST_WINDOW_MS = 10_000;
export const TOTAL_LIMIT = 600;
export const TOTAL_WINDOW_MS = 60_000;
export const BASE_BLOCK_MS = 60_000;
export const MAX_BLOCK_MS = 10 * 60_000;
export const TOTAL_BLOCK_MS = 30_000;

export const LOOP_GUARD_CODE = 'CLIENT_LOOP_GUARD';

export type TripInfo = {
  reason: 'same_request' | 'total';
  method: string;
  url: string;
  count: number;
  blockMs: number;
};

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

function bodyKey(body: unknown): string {
  if (body == null) return '';
  if (typeof body === 'string') return body.length > 2000 ? `${body.slice(0, 2000)}#${body.length}` : body;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return `blob:${body.size}`;
  if (body instanceof ArrayBuffer) return `buf:${body.byteLength}`;
  return `obj:${Object.prototype.toString.call(body)}`;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url;
}

function blockedResponse(info: TripInfo, retryInMs: number): Response {
  return new Response(
    JSON.stringify({
      code: LOOP_GUARD_CODE,
      message: 'This page sent the same request too many times, so it was paused to protect the database. Reload the page.',
      details: `${info.method} ${info.url.split('?')[0]}`,
      hint: `Retry in ${Math.ceil(retryInMs / 1000)}s`,
    }),
    { status: 429, headers: { 'Content-Type': 'application/json' } },
  );
}

export function createGuardedFetch(
  baseFetch: FetchLike,
  opts: { now?: () => number; onTrip?: (info: TripInfo) => void } = {},
): FetchLike {
  const now = opts.now ?? (() => Date.now());
  const recent = new Map<string, number[]>();
  const blockedUntil = new Map<string, number>();
  const trips = new Map<string, number>();
  let all: number[] = [];
  let allBlockedUntil = 0;

  const report = (info: TripInfo) => {
    try {
      opts.onTrip?.(info);
    } catch {
      // Reporting must never break the request path.
    }
  };

  return (input, init) => {
    const t = now();
    const method = (init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET')).toUpperCase();
    const url = requestUrl(input);
    const key = `${method} ${url} ${bodyKey(init?.body)}`;

    if (t < allBlockedUntil) {
      return Promise.resolve(blockedResponse({ reason: 'total', method, url, count: all.length, blockMs: TOTAL_BLOCK_MS }, allBlockedUntil - t));
    }
    const until = blockedUntil.get(key);
    if (until !== undefined && t < until) {
      return Promise.resolve(blockedResponse({ reason: 'same_request', method, url, count: 0, blockMs: until - t }, until - t));
    }

    all = all.filter((s) => t - s < TOTAL_WINDOW_MS);
    all.push(t);
    if (all.length > TOTAL_LIMIT) {
      allBlockedUntil = t + TOTAL_BLOCK_MS;
      const info: TripInfo = { reason: 'total', method, url, count: all.length, blockMs: TOTAL_BLOCK_MS };
      all = [];
      report(info);
      return Promise.resolve(blockedResponse(info, TOTAL_BLOCK_MS));
    }

    const stamps = (recent.get(key) ?? []).filter((s) => t - s < SAME_REQUEST_WINDOW_MS);
    stamps.push(t);
    if (stamps.length > SAME_REQUEST_LIMIT) {
      const n = (trips.get(key) ?? 0) + 1;
      trips.set(key, n);
      const blockMs = Math.min(BASE_BLOCK_MS * 2 ** (n - 1), MAX_BLOCK_MS);
      blockedUntil.set(key, t + blockMs);
      recent.delete(key);
      const info: TripInfo = { reason: 'same_request', method, url, count: stamps.length, blockMs };
      report(info);
      return Promise.resolve(blockedResponse(info, blockMs));
    }
    recent.set(key, stamps);

    // Keep the maps from growing without bound on long-lived tabs.
    if (recent.size > 2000) {
      for (const [k, v] of recent) {
        if (!v.length || t - v[v.length - 1]! >= SAME_REQUEST_WINDOW_MS) recent.delete(k);
      }
    }

    return baseFetch(input, init);
  };
}
